import { Card, Inline, Stack, Text, Tooltip, vars } from '@etg/wings';
import { InformationIconButton } from '@eti/components';
import { css } from '@eti/styles';
import { useProperty, useSnowplowTracking, useTranslation } from '@eti/providers';
import { PartOfExternalFlights } from '@eti/schema-types';
import type { ReactNode } from 'react';
import { useOrderDetails } from '../../containers/OrderDetailsContext';
import type { GroupedAirlineReferenceInfoType } from '../../utils';
import TextWithCopyButton from './TextWithCopyButton';

const numberBallStyles = css`
  background-color: #737373;
  border-radius: 50%;
  color: #fff;
  font-size: 0.75rem;
  height: 20px;
  line-height: 20px;
  text-align: center;
  width: 20px;
`;

const borderStyles = css`
  border: 1px solid ${vars.colors.divider};
`;

const flexStyles = css`
  flex: 1;
`;

interface AirlineReferenceInfoProps {
  airlineReferenceInfo: GroupedAirlineReferenceInfoType;
  dataTestId: string;
  descriptionContent: ReactNode;
  isContentReady?: boolean;
}

const AirlineReferenceInfo = ({
  airlineReferenceInfo,
  dataTestId,
  descriptionContent,
  isContentReady = true,
}: AirlineReferenceInfoProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const { partOfExternalFlights, ryanairLocale } = useOrderDetails();

  const handleSnowplowOnEmailInfo = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Order Details',
        action: 'Hover',
        label: 'EMAIL_INFORMATION',
      });
    }
  };

  const ryanairSteps = [...Array(3)].map((_, index) => ({
    text:
      index === 0
        ? t('OrderDetails.Guidelines.Ryanair.Step.1', ryanairLocale)
        : t(`OrderDetails.Guidelines.Ryanair.Step.${index + 1}`),
    isHtml: index === 0,
  }));
  const guidelinesSteps = [...Array(2)].map((_, index) => ({
    text:
      index === 0
        ? t(
            'OrderDetails.Guidelines.Step.1',
            airlineReferenceInfo.carrierBookingNumber?.carrierName,
          )
        : t(`OrderDetails.Guidelines.Step.${index + 1}`),
    isHtml: false,
  }));

  const steps =
    p('IbeClient.RyanairMarketingCarriers').includes(
      airlineReferenceInfo.carrierBookingNumber?.carrierCode,
    ) && partOfExternalFlights !== PartOfExternalFlights.None
      ? ryanairSteps
      : guidelinesSteps;

  return (
    <Stack data-testid={dataTestId}>
      <Stack spacing={8}>
        {descriptionContent}
        {steps.map((step, index) => (
          <Inline
            alignY="top"
            data-testid={`${dataTestId}-step-${index + 1}`}
            key={`step-${index}`}
            noWrap
            spacing={8}
          >
            {/* TODO: use common component that will be implemented in MYP-2241 */}
            <div className={numberBallStyles}>{index + 1}</div>
            <Text asHtml={step.isHtml} className={flexStyles}>
              {step.text}
            </Text>
          </Inline>
        ))}
      </Stack>
      <Stack spacing={8}>
        <Inline noWrap spacing={4}>
          <Text data-testid={`${dataTestId}-reference-label`}>
            {t('OrderDetails.AirlineReferenceNumber.Label')}
          </Text>
          {!airlineReferenceInfo.carrierBookingNumber?.bookingNumber && (
            <Tooltip
              content={t('OrderDetails.Airlines.BookingNumber.Unavailable.Text')}
              data-testid={`${dataTestId}-not-available-reference-tooltip`}
              title=""
            >
              <InformationIconButton data-testid={`${dataTestId}-unavailable-info-icon`} />
            </Tooltip>
          )}
        </Inline>
        <Card className={borderStyles}>
          {isContentReady && airlineReferenceInfo.carrierBookingNumber?.bookingNumber ? (
            <TextWithCopyButton
              data-testid={`${dataTestId}-reference-text`}
              justifyContentVariant="space-between"
              shouldApplyMouseflowMask
              snowplowInfo={{ category: 'Order Details', label: 'COPY_AIRLINE_REF' }}
              text={airlineReferenceInfo.carrierBookingNumber.bookingNumber}
            />
          ) : (
            <Text data-testid={`${dataTestId}-not-available-reference`}>
              {t('CheckIn.ReferenceNo.Unavailable.Text')}
            </Text>
          )}
        </Card>
      </Stack>
      <Stack spacing={8}>
        <Inline noWrap spacing={4}>
          <Text data-testid={`${dataTestId}-email-label`}>
            {t('OrderDetails.EmailAddress.Label')}
          </Text>
          {airlineReferenceInfo.emailInfo.isAlias && (
            <Tooltip
              content={t('OrderDetails.AliasEmail.Description')}
              data-testid="alias-email-tooltip"
              title={t('OrderDetails.AliasEmail.Title')}
            >
              <InformationIconButton
                data-testid={`${dataTestId}-alias-email-icon`}
                onMouseEnter={handleSnowplowOnEmailInfo}
              />
            </Tooltip>
          )}
        </Inline>
        <Card className={borderStyles}>
          {isContentReady && (
            <TextWithCopyButton
              data-testid={`${dataTestId}-email`}
              justifyContentVariant="space-between"
              shouldApplyMouseflowMask
              snowplowInfo={{ category: 'Order Details', label: 'EMAIL' }}
              text={airlineReferenceInfo.emailInfo.email}
            />
          )}
        </Card>
      </Stack>
    </Stack>
  );
};

export default AirlineReferenceInfo;
